/**
 * NewsPage Component
 * 
 * News section with filters and feed.
 * 
 * UI is fully implemented with shadcn components.
 * Backend API for news parsing and filtering is not yet implemented.
 * 
 * Features (ready for backend integration):
 * - Search by keywords
 * - GEO filter
 * - Category filter
 * - Tags filter (configurable chips)
 * - Time range filter
 * - News cards with title, description, image, source, date
 * - Clickable cards opening source in new tab
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, Link } from 'react-router-dom'
import {
    Search,
    Filter,
    Tag,
    Clock,
    ExternalLink,
    Newspaper,
    AlertCircle,
    ChevronDown,
    Loader2,
    Rss,
    Star,
    Link2
} from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card'
import { Badge } from '../../components/ui/badge'
import { Input } from '../../components/ui/input'
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator
} from '../../components/ui/dropdown-menu'
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/alert'
import { Switch } from '../../components/ui/switch'
import { Label } from '../../components/ui/label'
import { REGIONS, WORLDWIDE } from '../../constants/regions'
import { NEWS_CATEGORY_LABELS, NEWS_TAG_LABELS } from '../../constants/newsClassification'
import { cn } from '../../lib/utils'
import { ApiServices, newsService } from '../../services/ApiServices'
import GeoFlag from '../../components/GeoFlag'
import RegionCombobox from '../../components/RegionCombobox'

// Derived from newsClassification constants for the tags filter
const PLACEHOLDER_TAGS = Object.entries(NEWS_TAG_LABELS)
    .filter(([value]) => value !== 'other')
    .map(([value, label]) => ({ value, label }))

// Time filter options
const TIME_OPTIONS = [
    { value: '24h', label: 'Последние 24 часа' },
    { value: '3d', label: 'Последние 3 дня' },
    { value: '7d', label: 'Последние 7 дней' },
    { value: '30d', label: 'Последние 30 дней' },
]

// Helper to get region display label
const getRegionDisplayLabel = (value) => {
    if (value === 'all') return WORLDWIDE.name
    for (const group of REGIONS) {
        const country = group.countries.find((c) => c.code === value)
        if (country) return country.name
    }
    return WORLDWIDE.name
}

// Strong correlation threshold (SAME_GEO = 1.0)
const STRONG_CORRELATION_SCORE = 0.9

// News Card Component
const NewsCard = ({ article, showGeo, showCategory = true, showTags = true, navigate }) => {
    const [trendTooltipOpen, setTrendTooltipOpen] = useState(false)
    const trendHoverTimeoutRef = useRef(null)
    const trendBadgeRef = useRef(null)

    const formatDate = (dateStr) => {
        try {
            return new Date(dateStr).toLocaleString('ru-RU', {
                day: 'numeric',
                month: 'long',
                hour: '2-digit',
                minute: '2-digit'
            })
        } catch (e) {
            return dateStr
        }
    }

    const displayTitle = article.title_ru || article.title

    const handleCardClick = (e) => {
        const target = e.target
        const isTrendBadgeArea = target?.closest?.('[data-trend-badge-area]')
        if (isTrendBadgeArea) {
            e.stopPropagation()
            e.preventDefault()
            return
        }
        window.open(article.link, '_blank')
    }

    const leaveTimeoutRef = useRef(null)
    const handleTrendBadgeMouseEnter = () => {
        if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current)
        trendHoverTimeoutRef.current = setTimeout(() => setTrendTooltipOpen(true), 200)
    }
    const handleTrendBadgeMouseLeave = () => {
        if (trendHoverTimeoutRef.current) clearTimeout(trendHoverTimeoutRef.current)
        leaveTimeoutRef.current = setTimeout(() => setTrendTooltipOpen(false), 100)
    }
    const handleTrendTooltipMouseEnter = () => {
        if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current)
        setTrendTooltipOpen(true)
    }
    useEffect(() => () => {
        if (trendHoverTimeoutRef.current) clearTimeout(trendHoverTimeoutRef.current)
        if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current)
    }, [])

    return (
        <Card className="overflow-hidden flex flex-col hover:shadow-md transition-shadow cursor-pointer group" onClick={handleCardClick}>
            <div className="aspect-video relative bg-muted overflow-hidden">
                {article.image ? (
                    <img
                        src={article.image}
                        alt={displayTitle}
                        referrerPolicy="no-referrer"
                        className="object-cover w-full h-full transition-transform group-hover:scale-105"
                    />
                ) : (
                    <div className="flex items-center justify-center w-full h-full">
                        <Newspaper className="h-12 w-12 text-muted-foreground/30" />
                    </div>
                )}
                <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-2">
                    <div className="flex flex-col gap-1 shrink-0">
                        <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm w-fit">
                            {article.source_name}
                        </Badge>
                        {showGeo && article.geo && (
                            <GeoFlag geo={article.geo} className="bg-background/80 backdrop-blur-sm w-fit" />
                        )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                    {article.correlated_trends_count > 0 && (
                        <div
                            data-trend-badge-area
                            ref={trendBadgeRef}
                            className="shrink-0 relative"
                            onMouseEnter={handleTrendBadgeMouseEnter}
                            onMouseLeave={handleTrendBadgeMouseLeave}
                        >
                            <Badge className="bg-sky-500/90 text-sky-50 backdrop-blur-sm text-[10px] cursor-pointer hover:bg-sky-500">
                                {article.correlated_trends_count === 1
                                    ? 'Связан с трендом'
                                    : `Связан с ${article.correlated_trends_count} трендами`}
                            </Badge>
                            {trendTooltipOpen && trendBadgeRef.current && typeof document !== 'undefined' && createPortal(
                                <div
                                    id={'trend-tooltip-' + article.id}
                                    className="fixed z-[99999] min-w-[180px] max-w-[280px] rounded-md border border-border bg-popover p-1 shadow-xl"
                                    style={{
                                        top: trendBadgeRef.current.getBoundingClientRect().bottom + 4,
                                        left: Math.max(8, trendBadgeRef.current.getBoundingClientRect().right - 200),
                                    }}
                                    onMouseEnter={handleTrendTooltipMouseEnter}
                                    onMouseLeave={handleTrendBadgeMouseLeave}
                                >
                                    {Array.isArray(article.correlated_trends) && article.correlated_trends.map((t) => {
                                        const targetUrl = `/trend/${encodeURIComponent(t.query)}?geo=${t.geo || ''}`
                                        const isStrong = (t.score ?? 0.6) >= STRONG_CORRELATION_SCORE
                                        return (
                                            <Link
                                                key={t.id}
                                                to={targetUrl}
                                                className="flex items-center gap-2 cursor-pointer rounded-sm px-2 py-1.5 text-sm hover:bg-accent truncate"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {isStrong ? (
                                                    <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />
                                                ) : (
                                                    <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                                )}
                                                <span className="truncate">{t.query} {t.geo ? `(${t.geo})` : ''}</span>
                                            </Link>
                                        )
                                    })}
                                </div>,
                                document.body
                            )}
                        </div>
                    )}
                    {showCategory && article.category && (
                        <Badge className="bg-primary/90 text-primary-foreground backdrop-blur-sm text-[10px]">
                            {NEWS_CATEGORY_LABELS[article.category] || article.category}
                        </Badge>
                    )}
                    {showTags && Array.isArray(article.tags) && article.tags
                        .filter(t => t !== 'other')
                        .map(t => (
                            <Badge key={t} variant="secondary" className="bg-background/80 backdrop-blur-sm text-[10px] px-1.5 py-0 h-4">
                                {NEWS_TAG_LABELS[t] || t}
                            </Badge>
                        ))
                    }
                    </div>
                </div>
            </div>
            <CardContent className="p-4 flex-grow flex flex-col">
                <div className="flex-grow">
                    <h3 className="font-semibold leading-tight mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {displayTitle}
                    </h3>
                    {article.title_ru && (
                        <p className="text-xs text-muted-foreground/60 mb-1 line-clamp-1 italic">
                            {article.title}
                        </p>
                    )}
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                        {article.description}
                    </p>
                </div>
                <div className="mt-auto pt-4 border-t flex items-center justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(article.published_at)}
                    </span>
                    <span className="flex items-center gap-1 group-hover:text-primary transition-colors">
                        Источник
                        <ExternalLink className="h-3 w-3" />
                    </span>
                </div>
            </CardContent>
        </Card>
    )
}

// News Card Placeholder for loading state
const NewsCardPlaceholder = () => (
    <Card className="overflow-hidden flex flex-col h-full animate-pulse border-muted">
        <div className="aspect-video bg-muted" />
        <CardContent className="p-4 flex-grow flex flex-col gap-3">
            <div className="space-y-2">
                <div className="h-4 bg-muted rounded w-3/4" />
                <div className="h-4 bg-muted rounded w-1/2" />
            </div>
            <div className="space-y-2 flex-grow">
                <div className="h-3 bg-muted rounded w-full" />
                <div className="h-3 bg-muted rounded w-full" />
                <div className="h-3 bg-muted rounded w-2/3" />
            </div>
            <div className="pt-4 border-t flex justify-between mt-auto">
                <div className="h-3 bg-muted rounded w-1/4" />
                <div className="h-3 bg-muted rounded w-1/4" />
            </div>
        </CardContent>
    </Card>
)

const NewsPage = () => {
    const navigate = useNavigate()
    // Pagination Constants
    const ITEMS_PER_PAGE = 20
    const ITEMS_PER_BLOCK = 100

    // Filter state
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedGeo, setSelectedGeo] = useState('all')
    const [selectedCategory, setSelectedCategory] = useState('all')
    const [selectedTags, setSelectedTags] = useState([])
    const [selectedTime, setSelectedTime] = useState('7d')
    const [selectedSource, setSelectedSource] = useState('all')
    const [onlyWithCorrelation, setOnlyWithCorrelation] = useState(false)

    // Data state
    const [cachedNews, setCachedNews] = useState([]) // Stores all items fetched for current filters
    const [currentPage, setCurrentPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [hasMore, setHasMore] = useState(true)
    // When "only with correlation" is on: list from dedicated API call (null = not yet loaded, show cache first)
    const [correlatedOnlyList, setCorrelatedOnlyList] = useState(null)
    const [correlatedLoading, setCorrelatedLoading] = useState(false)

    // Fetch news data
    const fetchNews = async (isInitial = false) => {
        setLoading(true)
        setError(null)

        // Calculate offset based on how many items we already have
        // If it's a filter change (isInitial), start from 0
        const offset = isInitial ? 0 : cachedNews.length

        try {
            const data = await newsService.getLatestNews({
                geo: selectedGeo,
                category: selectedCategory,
                search: searchQuery,
                tags: selectedTags,
                limit: ITEMS_PER_BLOCK,
                offset: offset
            })

            const newItems = data || []

            if (isInitial) {
                setCachedNews(newItems)
                setCurrentPage(1)
            } else {
                setCachedNews(prev => [...prev, ...newItems])
            }

            // If we received fewer items than requested, there's no more data
            setHasMore(newItems.length === ITEMS_PER_BLOCK)
        } catch (err) {
            console.error('Failed to fetch news:', err)
            setError('Не удалось загрузить новости. Пожалуйста, попробуйте позже.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        ApiServices.trackAuditEvent('page_view', 'news');
    }, []);

    // Trigger initial fetch when filters change (including tags — same UX as geo/category/time/source)
    useEffect(() => {
        fetchNews(true)
    }, [selectedGeo, selectedCategory, selectedTime, selectedSource, selectedTags])

    // When "only with correlation" is off, clear dedicated correlated list
    useEffect(() => {
        if (!onlyWithCorrelation) setCorrelatedOnlyList(null)
    }, [onlyWithCorrelation])

    // When switch is on: request from API (only_with_correlation=1), show cache first then replace with API result
    const fetchCorrelatedOnly = async () => {
        setCorrelatedLoading(true)
        setCorrelatedOnlyList(null)
        try {
            const data = await newsService.getLatestNews({
                geo: selectedGeo !== 'all' ? selectedGeo : undefined,
                category: selectedCategory !== 'all' ? selectedCategory : undefined,
                search: searchQuery || undefined,
                tags: selectedTags.length > 0 ? selectedTags : undefined,
                only_with_correlation: true,
                limit: 500,
                offset: 0
            })
            setCorrelatedOnlyList(Array.isArray(data) ? data : [])
        } catch (err) {
            console.error('Failed to fetch correlated news:', err)
            setCorrelatedOnlyList([])
        } finally {
            setCorrelatedLoading(false)
        }
    }

    useEffect(() => {
        if (!onlyWithCorrelation) return
        setCurrentPage(1)
        fetchCorrelatedOnly()
    }, [onlyWithCorrelation, selectedGeo, selectedCategory, searchQuery, selectedTags])

    // Detect when we need to load more blocks as page changes (only when not in "correlated only" mode)
    useEffect(() => {
        if (onlyWithCorrelation) return
        const indexNeeded = (currentPage - 1) * ITEMS_PER_PAGE
        if (indexNeeded >= cachedNews.length && !loading && hasMore) {
            fetchNews(false)
        }
    }, [currentPage, onlyWithCorrelation, cachedNews.length, loading, hasMore])

    // Base list: normal mode = cachedNews; correlated mode = API list or cache filtered (while loading)
    const displayList = useMemo(() => {
        if (!onlyWithCorrelation) return cachedNews
        if (correlatedOnlyList !== null) return correlatedOnlyList
        return cachedNews.filter((n) => (n.correlated_trends_count || 0) > 0)
    }, [onlyWithCorrelation, cachedNews, correlatedOnlyList])

    // Get slice of news for the current page
    const paginatedNews = displayList.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    )

    const totalPages = onlyWithCorrelation
        ? Math.ceil(displayList.length / ITEMS_PER_PAGE) || 1
        : (hasMore ? Math.max(currentPage + 1, Math.ceil(cachedNews.length / ITEMS_PER_PAGE)) : Math.ceil(displayList.length / ITEMS_PER_PAGE))

    // Handle search on enter
    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            fetchNews(true)
        }
    }

    // Toggle tag selection
    const toggleTag = (tagValue) => {
        setSelectedTags(prev =>
            prev.includes(tagValue)
                ? prev.filter(t => t !== tagValue)
                : [...prev, tagValue]
        )
    }

    // Clear all filters
    const clearFilters = () => {
        setSearchQuery('')
        setSelectedGeo('all')
        setSelectedCategory('all')
        setSelectedTags([])
        setSelectedTime('7d')
        setSelectedSource('all')
        setOnlyWithCorrelation(false)
    }

    const hasActiveFilters = searchQuery || selectedGeo !== 'all' || selectedCategory !== 'all' || selectedTags.length > 0 || selectedTime !== '7d' || selectedSource !== 'all' || onlyWithCorrelation

    // Derive unique source names from cached data for the source filter
    const availableSources = useMemo(() => {
        const sourcesMap = new Map()
        cachedNews.forEach(item => {
            if (item.source_name) {
                const key = `${item.source_name}|${item.geo || ''}`
                if (!sourcesMap.has(key)) {
                    sourcesMap.set(key, { name: item.source_name, geo: item.geo })
                }
            }
        })
        return Array.from(sourcesMap.values()).sort((a, b) => a.name.localeCompare(b.name))
    }, [cachedNews])

    // Helper for source display name
    const getSourceDisplayName = (name, geo) => {
        if (!name) return 'Неизвестный источник'
        if (selectedGeo === 'all' && geo) {
            return `${name} - ${geo.toUpperCase()}`
        }
        return name
    }

    // Apply client-side source filter on top of paginated data
    const filteredPaginatedNews = selectedSource === 'all'
        ? paginatedNews
        : displayList.filter(item => `${item.source_name}|${item.geo || ''}` === selectedSource)

    return (
        <div className="space-y-6">
            {/* Header with inline filters */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Новости</h1>
                    <p className="text-muted-foreground">
                        Последние новости со всего мира
                    </p>
                </div>

                {/* Inline Filters: row 1 = search + dropdowns, row 2 = switch + refresh + clear (адаптивно) */}
                <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Поиск..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleSearchKeyPress}
                            className="pl-9 w-40 min-w-0"
                        />
                    </div>

                    {/* GEO Filter */}
                    <RegionCombobox value={selectedGeo} onChange={setSelectedGeo} className="h-9 w-[220px] px-3" />

                    {/* Source Filter */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-2">
                                <Rss className="h-4 w-4" />
                                {selectedSource === 'all'
                                    ? 'Источник'
                                    : (() => {
                                        const [name, geo] = selectedSource.split('|')
                                        return getSourceDisplayName(name, geo)
                                    })()
                                }
                                <ChevronDown className="h-4 w-4 opacity-50" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="max-h-[300px] overflow-y-auto w-56">
                            <DropdownMenuItem onClick={() => setSelectedSource('all')}>
                                Все источники
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {availableSources.map((sourceObj) => {
                                const key = `${sourceObj.name}|${sourceObj.geo || ''}`
                                return (
                                    <DropdownMenuItem
                                        key={key}
                                        onClick={() => setSelectedSource(key)}
                                    >
                                        {getSourceDisplayName(sourceObj.name, sourceObj.geo)}
                                        {selectedSource === key && (
                                            <Badge variant="default" className="ml-auto h-1.5 w-1.5 rounded-full p-0" />
                                        )}
                                    </DropdownMenuItem>
                                )
                            })}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Category Filter */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-2">
                                <Filter className="h-4 w-4" />
                                {selectedCategory === 'all' ? 'Категория' : NEWS_CATEGORY_LABELS[selectedCategory] || selectedCategory}
                                <ChevronDown className="h-4 w-4 opacity-50" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-48">
                            <DropdownMenuItem onClick={() => setSelectedCategory('all')}>
                                Все категории
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {Object.entries(NEWS_CATEGORY_LABELS).map(([key, label]) => (
                                <DropdownMenuItem key={key} onClick={() => setSelectedCategory(key)}>
                                    {label}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Tags Filter */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-2">
                                <Tag className="h-4 w-4" />
                                {selectedTags.length === 0 ? 'Теги' : `Теги (${selectedTags.length})`}
                                <ChevronDown className="h-4 w-4 opacity-50" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-56">
                            {PLACEHOLDER_TAGS.map((tag) => (
                                <DropdownMenuItem
                                    key={tag.value}
                                    onClick={() => toggleTag(tag.value)}
                                    className="flex items-center justify-between"
                                >
                                    {tag.label}
                                    {selectedTags.includes(tag.value) && (
                                        <Badge variant="default" className="ml-2 h-1.5 w-1.5 rounded-full p-0" />
                                    )}
                                </DropdownMenuItem>
                            ))}
                            {selectedTags.length > 0 && (
                                <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => setSelectedTags([])} className="text-destructive">
                                        Очистить теги
                                    </DropdownMenuItem>
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Time Filter */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-2">
                                <Clock className="h-4 w-4" />
                                {TIME_OPTIONS.find(t => t.value === selectedTime)?.label}
                                <ChevronDown className="h-4 w-4 opacity-50" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-48">
                            {TIME_OPTIONS.map((option) => (
                                <DropdownMenuItem
                                    key={option.value}
                                    onClick={() => setSelectedTime(option.value)}
                                >
                                    {option.label}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    </div>
                    {/* Second row: switch + refresh + clear — выравнивание по правому краю */}
                    <div className="flex flex-wrap items-center gap-2 flex-shrink-0 justify-end">
                    {/* Only with correlation to trends */}
                    <div className="flex items-center gap-2">
                        <Switch
                            id="news-only-correlated"
                            checked={onlyWithCorrelation}
                            onCheckedChange={(checked) => {
                                setOnlyWithCorrelation(checked)
                                setCurrentPage(1)
                            }}
                        />
                        <Label htmlFor="news-only-correlated" className="text-sm cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                            <Link2 className="h-4 w-4 text-muted-foreground" />
                            Только со связью с трендами
                        </Label>
                    </div>

                    {/* Refresh Button */}
                    <Button variant="ghost" size="icon" onClick={fetchNews} disabled={loading} className="h-9 w-9">
                        <Loader2 className={cn("h-4 w-4", loading && "animate-spin")} />
                    </Button>

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                        <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
                            Очистить
                        </Button>
                    )}
                    </div>
                </div>
            </div>

            {/* Active Filters Summary */}
            {hasActiveFilters && (
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Фильтры:</span>
                    {searchQuery && (
                        <Badge variant="secondary" className="gap-1 pr-1">
                            Поиск: {searchQuery}
                            <Button variant="ghost" size="icon" className="h-3.5 w-3.5 ml-1 p-0 hover:bg-transparent" onClick={() => setSearchQuery('')}>×</Button>
                        </Badge>
                    )}
                    {selectedGeo !== 'all' && (
                        <Badge variant="secondary" className="gap-1 pr-1">
                            ГЕО: {getRegionDisplayLabel(selectedGeo)}
                            <Button variant="ghost" size="icon" className="h-3.5 w-3.5 ml-1 p-0 hover:bg-transparent" onClick={() => setSelectedGeo('all')}>×</Button>
                        </Badge>
                    )}
                    {selectedCategory !== 'all' && (
                        <Badge variant="secondary" className="gap-1 pr-1">
                            {NEWS_CATEGORY_LABELS[selectedCategory] || selectedCategory}
                            <Button variant="ghost" size="icon" className="h-3.5 w-3.5 ml-1 p-0 hover:bg-transparent" onClick={() => setSelectedCategory('all')}>×</Button>
                        </Badge>
                    )}
                    {selectedTags.map(tagValue => (
                        <Badge key={tagValue} variant="secondary" className="gap-1 pr-1">
                            {PLACEHOLDER_TAGS.find(t => t.value === tagValue)?.label}
                            <Button variant="ghost" size="icon" className="h-3.5 w-3.5 ml-1 p-0 hover:bg-transparent" onClick={() => toggleTag(tagValue)}>×</Button>
                        </Badge>
                    ))}
                    {selectedSource !== 'all' && (
                        <Badge variant="secondary" className="gap-1 pr-1">
                            Источник: {(() => {
                                const [name, geo] = selectedSource.split('|')
                                return getSourceDisplayName(name, geo)
                            })()}
                            <Button variant="ghost" size="icon" className="h-3.5 w-3.5 ml-1 p-0 hover:bg-transparent" onClick={() => setSelectedSource('all')}>×</Button>
                        </Badge>
                    )}
                    {onlyWithCorrelation && (
                        <Badge variant="secondary" className="gap-1 pr-1">
                            Со связью с трендами
                            <Button variant="ghost" size="icon" className="h-3.5 w-3.5 ml-1 p-0 hover:bg-transparent" onClick={() => setOnlyWithCorrelation(false)}>×</Button>
                        </Badge>
                    )}
                </div>
            )}

            {/* When "only with correlation" is loading: show hint that full list is loading */}
            {onlyWithCorrelation && correlatedLoading && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Загружаем полный список новостей со связью с трендами…
                </p>
            )}

            {/* Error state */}
            {error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Ошибка</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            {/* News Grid */}
            {loading && cachedNews.length === 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                        <NewsCardPlaceholder key={i} />
                    ))}
                </div>
            ) : filteredPaginatedNews.length > 0 ? (
                <div className="space-y-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filteredPaginatedNews.map((article) => (
                            <NewsCard key={article.id} article={article} showGeo showCategory={selectedCategory === 'all'} showTags={selectedTags.length === 0} navigate={navigate} />
                        ))}
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex flex-col items-center gap-4 py-4 border-t">
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1 || loading}
                            >
                                Назад
                            </Button>

                            <div className="flex items-center gap-1">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    // Simple logic to show a few pages around current
                                    let pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i
                                    if (pageNum > totalPages && totalPages > 0) return null

                                    return (
                                        <Button
                                            key={pageNum}
                                            variant={currentPage === pageNum ? "default" : "ghost"}
                                            size="sm"
                                            className="h-8 w-8 p-0"
                                            onClick={() => setCurrentPage(pageNum)}
                                            disabled={loading}
                                        >
                                            {pageNum}
                                        </Button>
                                    )
                                }).filter(Boolean)}
                                {totalPages > 5 && currentPage < totalPages - 2 && (
                                    <span className="text-muted-foreground px-1">...</span>
                                )}
                            </div>

                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                disabled={(!hasMore && currentPage >= totalPages) || loading}
                            >
                                Вперед
                                {loading && <Loader2 className="ml-2 h-3 w-3 animate-spin" />}
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Страница {currentPage} из {totalPages || 1} • Показано {paginatedNews.length} из {displayList.length} {onlyWithCorrelation ? 'со связью' : 'загруженных'}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="space-y-8 py-10">
                    <div className="space-y-2 text-center max-w-lg mx-auto">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-4">
                            <Newspaper className="h-6 w-6 text-muted-foreground/50" />
                        </div>
                        <h3 className="text-2xl font-bold tracking-tight">Нет новостей</h3>
                        <p className="text-muted-foreground">Мы не нашли подходящих материалов</p>
                        <p className="text-sm text-muted-foreground/70 mt-4 bg-muted/30 py-4 px-6 rounded-lg border border-dashed">
                            Ничего не найдено по вашему запросу. Попробуйте изменить параметры фильтрации или поисковый запрос.
                        </p>
                        {hasActiveFilters && (
                            <Button variant="outline" className="mt-6" onClick={clearFilters}>
                                Сбросить все фильтры
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default NewsPage
